// src/utils/fcmNotifications.js
import { messaging, getToken, onMessage } from '../firebaseConfig';
import { getFirestore, doc, updateDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { firebaseApp } from '../firebaseConfig';
import { sendBrowserNotification, requestNotificationPermission } from './browserNotifications';

// VAPID Key from Firebase Console
const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY;

// Obter instância do Firestore
const db = getFirestore(firebaseApp);

/**
 * Request permission and get FCM token
 * @param {string} userId - User's UID for identification
 * @returns {Promise<string|null>} - FCM token or null
 */
export const requestFCMToken = async (userId) => {
    if (!("Notification" in window)) {
        console.log('This browser does not support desktop notifications');
        return null;
    }

    try {
        // Primeiro, solicitar permissão usando nossa função
        const hasPermission = await requestNotificationPermission();

        if (!hasPermission) {
            console.log('❌ Notification permission denied');
            return null;
        }

        // Se a permissão foi concedida, obter o token
        const currentToken = await getToken(messaging, {
            vapidKey: VAPID_KEY
        });

        if (currentToken) {
            console.log('✅ FCM Token generated successfully');
            return currentToken;
        } else {
            console.log('❌ No registration token available');
            return null;
        }
    } catch (error) {
        console.error('❌ Error retrieving FCM token:', error);
        return null;
    }
};

/**
 * Listen for foreground messages (when app is open)
 * @param {Function} callback - Function to handle incoming messages
 * @returns {Function} - Unsubscribe function
 */
export const onForegroundMessage = (callback) => {
    return onMessage(messaging, (payload) => {
        console.log('📩 Foreground message received:', payload);

        // Call the provided callback with the payload
        if (callback) callback(payload);

        // Extract notification data
        const notificationTitle = payload.notification?.title || 'Nova Notificação';
        const notificationBody = payload.notification?.body || '';
        const ticketId = payload.data?.ticketId || '';
        const url = payload.data?.url || '/helpdesk/gerenciamento-de-tickets';

        // Show native notification using our browser notification utility
        sendBrowserNotification(
            notificationTitle,
            notificationBody,
            ticketId ? `${url}?ticketId=${ticketId}` : url
        );
    });
};

/**
 * Save FCM token to user's Firestore document
 * @param {string} userId - User's UID
 * @param {string} token - FCM token
 */
export const saveFCMTokenToUser = async (userId, token) => {
    if (!userId || !token) {
        console.warn('⚠️ UserId or token missing');
        return;
    }

    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            fcmToken: token,
            fcmTokenUpdatedAt: new Date(),
            fcmTokenEnabled: true
        });
        console.log('✅ FCM token saved to user document');
    } catch (error) {
        console.error('❌ Error saving FCM token:', error);

        // Se o documento não existir, tentar criar
        if (error.code === 'not-found') {
            try {
                await setDoc(doc(db, 'users', userId), {
                    fcmToken: token,
                    fcmTokenUpdatedAt: new Date(),
                    fcmTokenEnabled: true
                }, { merge: true });
                console.log('✅ FCM token saved to new user document');
            } catch (setError) {
                console.error('❌ Error creating user document:', setError);
            }
        }
    }
};

/**
 * Get user by email to find FCM token
 * @param {string} email - User's email
 * @returns {Promise<{id: string, data: any} | null>}
 */
export const getUserByEmail = async (email) => {
    if (!email) return null;

    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', email));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            console.log(`❌ User not found: ${email}`);
            return null;
        }

        const doc = querySnapshot.docs[0];
        return {
            id: doc.id,
            data: doc.data()
        };
    } catch (error) {
        console.error('❌ Error finding user:', error);
        return null;
    }
};

/**
 * Check FCM status for debugging
 */
export const checkFCMStatus = async () => {
    console.log('🔍 Verificando status do FCM...');

    const status = {
        notificationSupported: false,
        permission: 'default',
        swRegistered: false,
        hasToken: false,
        token: null
    };

    // 1. Verificar suporte a notificações
    if (!("Notification" in window)) {
        console.log('❌ Notificações não suportadas');
        status.notificationSupported = false;
        return status;
    }

    status.notificationSupported = true;
    status.permission = Notification.permission;
    console.log(`📊 Permission status: ${Notification.permission}`);

    // 2. Verificar service worker
    if ('serviceWorker' in navigator) {
        try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            status.swRegistered = registrations.length > 0;

            const firebaseSW = registrations.find(reg =>
                reg.active?.scriptURL?.includes('firebase-messaging-sw.js')
            );

            if (firebaseSW) {
                console.log('✅ Firebase Messaging Service Worker encontrado!');
            } else if (registrations.length > 0) {
                console.log('⚠️ Service Worker encontrado, mas não é do Firebase');
            } else {
                console.log('⚠️ Nenhum Service Worker registrado');
            }
        } catch (error) {
            console.error('❌ Erro ao verificar Service Worker:', error);
        }
    } else {
        console.log('❌ Service Workers não suportados');
    }

    // 3. Verificar token
    try {
        const token = await getToken(messaging, { vapidKey: VAPID_KEY });
        if (token) {
            status.hasToken = true;
            status.token = token;
            console.log(`✅ Token FCM encontrado: ${token.substring(0, 20)}...`);
        } else {
            console.log('❌ Nenhum token FCM disponível');
        }
    } catch (error) {
        console.error('❌ Erro ao obter token:', error);
    }

    return status;
};